import type { CarePlan, Goal, ServiceRequest } from '@medplum/fhirtypes';
import React from 'react';

import { Grid, Icon, Stack, Text } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';

import { type CarePlanList, carePlanToken } from '../plan-list';

import { CarePlanCard } from './CarePlanCard';
import { VisitScheduleCard } from './VisitScheduleCard';

/**
 * The presentational root of the Care Plans section (ENG-5369): the section
 * label, one card per active plan (stacked), the visit-schedule card, and
 * the loading / failed / no-plan states. Pure presentation over the derived
 * `CarePlanList` — data fetching and URL routing live in the section.
 */
export type CarePlansOverviewProps = {
  list: CarePlanList;
  /** The loaded request window phase states resolve from. */
  serviceRequests: ServiceRequest[];
  /** The loaded goal window plan goal references resolve against. */
  goals: Goal[];
  todayIsoDate: string;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Token of the expanded card (`?plan=<id>`), or null when all collapsed. */
  expandedToken: string | null;
  onToggle: (token: string) => void;
  onOpenCondition: (token: string) => void;
  onOpenGoal: (token: string) => void;
};

const CardShellNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <section
    data-testid="care-plans-note"
    className="rounded-lg border border-border-primary bg-bg-primary"
  >
    <Grid place="center" className="px-6 py-12">
      <Text size="sm" color="tertiary" weight="regular">
        {children}
      </Text>
    </Grid>
  </section>
);

/** Compact no-active-plan banner (Rev7). Text-only — no creation flow. */
const NoCarePlanNote: React.FC<{ hasDraft: boolean }> = ({ hasDraft }) => (
  <Stack
    direction="horizontal"
    gap={3}
    align="center"
    data-testid="no-care-plan-note"
    className="rounded-lg border border-border-primary bg-bg-primary px-4 py-3"
  >
    <Icon name="Info" size="sm" className="shrink-0 text-text-tertiary" />
    <Text inline size="sm" className="min-w-0 flex-1">
      <Text inline size="sm" weight="semibold">
        No active care plan.
      </Text>{' '}
      {hasDraft
        ? 'A drafted plan is awaiting review.'
        : "This patient's conditions and goals aren't organized under a plan yet."}
    </Text>
  </Stack>
);

export const CarePlansOverview: React.FC<CarePlansOverviewProps> = ({
  list,
  serviceRequests,
  goals,
  todayIsoDate,
  loading,
  failed,
  paging,
  expandedToken,
  onToggle,
  onOpenCondition,
  onOpenGoal,
}) => {
  if (failed) return <CardShellNote>Couldn't load care plans.</CardShellNote>;
  if (loading) return <CardShellNote>Loading care plans…</CardShellNote>;
  if (list.active.length === 0) {
    return <NoCarePlanNote hasDraft={list.drafts.length > 0} />;
  }

  return (
    <Stack gap={4} align="stretch" data-testid="care-plans-section">
      <Text
        size="xs"
        color="tertiary"
        weight="semibold"
        className="-mb-1 px-4 tracking-wide uppercase"
      >
        {list.active.length > 1 ? 'Care plans' : 'Care plan'}
      </Text>
      {list.active.map((plan: CarePlan) => (
        <CarePlanCard
          key={carePlanToken(plan)}
          plan={plan}
          serviceRequests={serviceRequests}
          goals={goals}
          todayIsoDate={todayIsoDate}
          expanded={expandedToken === carePlanToken(plan)}
          onToggle={onToggle}
          onOpenCondition={onOpenCondition}
          onOpenGoal={onOpenGoal}
        />
      ))}
      {list.active[0] && (
        <VisitScheduleCard
          plan={list.active[0]}
          serviceRequests={serviceRequests}
          todayIsoDate={todayIsoDate}
        />
      )}
      <LoadMoreRow paging={paging} noun="care plans" />
    </Stack>
  );
};
